import { z } from 'zod';
import { getLocale } from '@budgeting101/i18n';
import { api } from './api.js';

const VapidKeySchema = z.object({
  publicKey: z.string(),
  enabled: z.boolean(),
}).strict();

const SubscriptionSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(['ACTIVE', 'EXPIRED', 'REVOKED']),
  locale: z.enum(['en', 'ar']),
  createdAt: z.string(),
}).strict();

export type PushState =
  | 'unsupported'
  | 'unavailable'
  | 'blocked'
  | 'enabled'
  | 'disabled';

/**
 * The VAPID public key must be handed to the browser as raw bytes, not the
 * URL-safe base64 the server stores it as.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalised);
  // Backed by an explicit ArrayBuffer: applicationServerKey requires a
  // BufferSource, which excludes a SharedArrayBuffer-backed view.
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

/**
 * iOS only exposes push to a PWA that has been added to the Home Screen. A user
 * on Safari/iPhone sees the toggle do nothing otherwise, so the UI has to explain
 * this rather than appear broken.
 */
export function iosNeedsInstall(): boolean {
  if (!pushSupported()) return false;
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone = window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return isIos && !standalone;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  return navigator.serviceWorker.ready;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

export async function readPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const { enabled } = await api.get('/notifications/vapid-public-key', VapidKeySchema);
  if (!enabled) return 'unavailable';
  if (Notification.permission === 'denied') return 'blocked';
  return (await currentSubscription()) ? 'enabled' : 'disabled';
}

/**
 * Ask for permission and register this device.
 *
 * `Notification.requestPermission()` must be called from a user gesture, and a
 * denial is permanent from script — a browser will not re-prompt. That is why
 * this is only ever reached from an explicit toggle, never on page load.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const { publicKey, enabled } = await api.get('/notifications/vapid-public-key', VapidKeySchema);
  if (!enabled || !publicKey) return 'unavailable';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'disabled';

  const reg = await registration();
  if (!reg) return 'unsupported';

  const existing = await reg.pushManager.getSubscription();
  const subscription = existing ?? await reg.pushManager.subscribe({
    // Required: a push service will not deliver a payload to an unauthenticated
    // subscription, and Chrome refuses to subscribe without it.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return 'disabled';

  await api.post('/notifications/subscribe', SubscriptionSchema, {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    locale: getLocale(),
    userAgent: navigator.userAgent.slice(0, 500),
  });
  return 'enabled';
}

/**
 * Revoke this device.
 *
 * Both halves matter. The browser unsubscribe stops the push service delivering,
 * and the server call releases the row — a push endpoint identifies a BROWSER,
 * not a person, so a stale row would let whoever signs in next receive the
 * previous user's notifications.
 */
export async function disablePush(): Promise<PushState> {
  const subscription = await currentSubscription();
  if (!subscription) return 'disabled';
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => undefined);
  await api.post('/notifications/unsubscribe', z.unknown(), { endpoint }).catch(() => undefined);
  return 'disabled';
}

/** Keep the device's notification language in step with the app's. */
export async function syncPushLocale(): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;
  await api.patch('/notifications/subscription/locale', SubscriptionSchema, {
    endpoint: subscription.endpoint,
    locale: getLocale(),
  }).catch(() => undefined);
}
